import React, { Suspense, useMemo, useState } from 'react';
import {
  FileText,
  FileEdit,
  SquareTerminal,
  Search,
  Globe,
  Bot,
  Wrench,
  Trash2,
  ArrowRight,
  RefreshCw,
  Clock,
  Zap,
  GitBranch,
  GitCommitHorizontal,
  GitCompare,
  GitPullRequest,
  ArrowUpFromLine,
  ArrowDownToLine,
  Replace,
  FolderOpen,
  FilePlus,
  Copy,
  ExternalLink,
  FlaskConical,
  Hammer,
  Package,
  Tag,
  CodeXml,
  Timer,
} from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { ScannedPath } from '@reflex/ui/components/content/ScannedPath';
import { ScannedUrl } from '@reflex/ui/components/content/ScannedUrl';
import {
  absoluteFilePath,
  useWorkingDir,
  WorkingDirProvider,
} from '@reflex/ui/components/content/WorkingDirContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { AgentRepoChip } from '@reflex/ui/components/agent/AgentRepoChip';
import { Badge } from '@reflex/ui/components/ui/badge';
import {
  RESOURCE_PREVIEW_HOVER_CONTENT_CLASS,
  ResourcePreviewCard,
} from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { formatDuration } from '@reflex/ui/lib/format';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import { AnsiText } from '@reflex/ui/components/content/AnsiText';
import { CopyButton as CopyIconButton } from '@reflex/ui/components/shared/CopyButton';
import { useGitHubWebBaseUrl, useOrgKey, usePlugins } from '@reflex/ui/client/hooks';
import { useQuery } from '@tanstack/react-query';
import { getAgent as apiGetAgent } from '@reflex/ui/client/generated/agents';
import { queryKeys } from '@reflex/ui/client/query-keys';
import { useHumanReadableCommands } from '@reflex/ui/client/prefs';
import { getGitRepoAttachment, parseGitHubPrUrls } from '@reflex/shared';
import type { ContentItem, ToolCallBlock, ToolKind } from '../types.js';
import { normalizeToolName, parseMcpToolName } from '../launched-agent.js';
import { formatMcpArgs } from '../mcp-args.js';
import { writeFileInput } from '../write-file-input.js';
import { capReadLines, numberLines, shellFileOpFromInput } from '../shell-file-ops.js';
import {
  shellSummaryFromInput,
  type ShellStep,
  type ShellSubject,
  type ShellSummaryIcon,
} from '../shell-summary.js';
import { verb, type Tense, type VerbWords } from '../verb-forms.js';
import {
  parseGitRefSubject,
  type GitRefKind,
  type GitRefLink,
  type GitRefSubject,
} from '../git-ref-links.js';
import { gistOwnerUrl, gistWebUrl, parseGistSubject, type GistRef } from '../gist-links.js';
import { useShellParser } from '../shell-parser.js';
import { readBeforeWriteRetryOf, type ReadBeforeWriteRetry } from '../read-before-write.js';
import { pathArgument } from '../tool-path-arguments.js';
import { toolCallTitleLabel } from '../acp-title.js';
import { relativeToWorkingDir } from './format-path.js';
import { getDiffLineCounts, getDiffSizeIssue } from './diff-utils.js';
import {
  toolKindMeta,
  CopyButton,
  ToolCallContentView,
  ToolCallErrorView,
  ReadOutputView,
  parseReadOutput,
  InlineHighlightedCode,
  DiffLineCountBadge,
  isImagePath,
} from './shared.js';
import { formatShellCommand, unwrapShellCommand, type ShellTimeout } from './shell-command.js';
import { AnimatedCollapse } from './AnimatedCollapse.js';

/**
 * How much of a call's output is searched for pull-request links, and how many
 * of them the row will show. A tool row is a headline, not a list: the cap is
 * what keeps `gh pr list` from turning one row into a page, and the character
 * bound keeps the scan off the tail of a megabyte of logs.
 */
const PR_LINK_SCAN_LIMIT = 20_000;
const MAX_PR_LINK_CHIPS = 2;

const TOOL_ICONS: Record<string, React.ElementType> = {
  Bash: SquareTerminal,
  Shell: SquareTerminal,
  Read: FileText,
  ReadNotebook: FileText,
  Write: FileEdit,
  // Synthesized for a shell command that only adds to a file — the glyph says
  // "more file", so an append is distinguishable from an overwrite at a glance.
  Append: FilePlus,
  Edit: FileEdit,
  EditNotebook: FileEdit,
  StrReplace: FileEdit,
  Grep: Search,
  Glob: Search,
  ToolSearch: Search,
  SemanticSearch: Search,
  WebFetch: Globe,
  WebSearch: Globe,
  Agent: Bot,
  Task: Bot,
  Skill: Zap,
  Delete: Trash2,
  Move: ArrowRight,
  SwitchMode: RefreshCw,
};

/**
 * Icons for a summarized shell row. {@link ../shell-summary.js} names the slot,
 * the view picks the glyph, so the summariser stays free of React.
 */
const SHELL_SUMMARY_ICONS: Record<ShellSummaryIcon, React.ElementType> = {
  search: Search,
  git: GitBranch,
  commit: GitCommitHorizontal,
  branch: GitBranch,
  diff: GitCompare,
  push: ArrowUpFromLine,
  pull: ArrowDownToLine,
  replace: Replace,
  read: FileText,
  folder: FolderOpen,
  'file-plus': FilePlus,
  trash: Trash2,
  move: ArrowRight,
  copy: Copy,
  test: FlaskConical,
  build: Hammer,
  package: Package,
  pr: GitPullRequest,
  gist: CodeXml,
  globe: Globe,
  terminal: SquareTerminal,
};

/**
 * The verbs a native tool row leads with. Each is named once and carries all
 * three tenses ({@link ../verb-forms.js}), so "Wrote src/app.ts" can't end up
 * over a write that never landed: a failed call gets its own words rather than
 * the completed ones with a red chip bolted on.
 *
 * The verbs are shared rather than repeated because the tables are: the tools
 * that read a file all read it, and a tool kind describes the same act its
 * tools do.
 */
const RUN = verb('run');
const READ = verb('read');
const WRITE = verb('write');
const EDIT = verb('edit');
const SEARCH = verb('search');
const FETCH = verb('fetch');
const DELETE = verb('delete');
const MOVE = verb('move');
const SWITCH_MODE = verb('switch mode');

/** Tool-name verbs, the first link in the row's name → kind → raw-title chain. */
const TOOL_VERBS: Record<string, VerbWords> = {
  Bash: RUN,
  Shell: RUN,
  Read: READ,
  ReadNotebook: READ,
  Write: WRITE,
  Append: verb('append to'),
  Edit: EDIT,
  EditNotebook: EDIT,
  StrReplace: EDIT,
  Grep: SEARCH,
  Glob: SEARCH,
  ToolSearch: SEARCH,
  SemanticSearch: SEARCH,
  WebFetch: FETCH,
  WebSearch: SEARCH,
  Agent: verb('invoke Agent', { failed: 'Agent failed' }),
  Task: verb('invoke Task', { failed: 'Task failed' }),
  Skill: verb('use skill'),
  Delete: DELETE,
  Move: MOVE,
  SwitchMode: SWITCH_MODE,
};

/** Tool-kind verbs, for a tool whose own name we have no words for. */
const KIND_VERBS: Record<ToolKind, VerbWords | null> = {
  execute: RUN,
  read: READ,
  edit: EDIT,
  delete: DELETE,
  move: MOVE,
  search: SEARCH,
  // "Thought" claims a conclusion the row can't see, so thinking reads the same
  // whether it finished or is still going.
  think: { done: 'Thinking', active: 'Thinking', failed: "Couldn't finish thinking" },
  fetch: FETCH,
  switch_mode: SWITCH_MODE,
  other: null,
};

function getIcon(block: ToolCallBlock): React.ElementType {
  return TOOL_ICONS[block.title] ?? toolKindMeta(block.kind).icon ?? Wrench;
}

/**
 * The row's leading words, and whether they say the call failed. A tool we have
 * no words for falls back to its bare title in every tense, so the caller can't
 * assume a failed row reads as one — it asks.
 */
interface RowPrefix {
  text: string;
  saysFailed: boolean;
}

function getPrefix(block: ToolCallBlock, tense: Tense): RowPrefix {
  const words = TOOL_VERBS[block.title] ?? KIND_VERBS[block.kind];
  if (!words) return { text: block.title, saysFailed: false };
  return { text: words[tense], saysFailed: tense === 'failed' };
}

interface DisplayInfo {
  code?: string;
  /**
   * Expanded form of `code` for the overflow tooltip and its copy action. Set
   * only when the row lost something worth recovering — a shell command's real
   * line breaks — never merely a re-spaced copy of what the row already shows.
   */
  expandedCode?: string;
  /**
   * Interpreter stripped off `code` (`/bin/bash -lc`). The row is better
   * without it, but a reader inspecting the call should still be able to see
   * how the command was invoked, so the hover panel carries it.
   */
  interpreter?: string;
  secondary?: string;
  /** True when `code` is a filesystem path — clipping keeps the tail, since the basename is the informative part. */
  isPath?: boolean;
  /** Shiki language id for `code` when it's meaningfully highlightable (e.g. a shell command). */
  lang?: string;
  /**
   * Render `code` as a tinted accent chip rather than muted mono text. Used for
   * a Skill's name so it reads as a distinct, named capability — matching the
   * violet the session init block already uses for skill/capability tags.
   */
  accent?: boolean;
  /**
   * Free text the call was invoked with (a Skill's `args`). Shown after `code`
   * on the row, clipped, and in full in the expanded body.
   */
  args?: string;
  /** Time limit a `timeout …` prefix put on the command, when `code` dropped one. */
  timeout?: ShellTimeout;
}

function getDisplayInfo(block: ToolCallBlock, input: Record<string, unknown> | null): DisplayInfo {
  if (input) {
    if (typeof input.command === 'string') {
      // Agents that shell out report the argv they ran (`/bin/bash -lc "…"`),
      // whose boilerplate would otherwise consume the row and push the real
      // command into the ellipsis. Show the command itself instead.
      const command = formatShellCommand(input.command);
      return {
        code: command.row,
        expandedCode: command.full,
        interpreter: command.interpreter,
        timeout: command.timeout,
        secondary: typeof input.description === 'string' ? input.description : undefined,
        lang: 'bash',
      };
    }
    // A Skill call carries the invoked skill name in `skill` (its command
    // equivalent) and the free text it was invoked with in `args`. Surface both
    // so the row reads e.g. "Used skill register-pr <pr url>" instead of a bare,
    // nameless "Skill". Unlike a Bash call's description — which only restates
    // the command the row already shows — a skill's args are the only record of
    // what it was pointed at, so they earn a place on the row and a panel of
    // their own in the expanded body.
    if (typeof input.skill === 'string')
      return {
        code: input.skill,
        accent: true,
        args: typeof input.args === 'string' && input.args.trim() ? input.args : undefined,
      };
    if (typeof input.pattern === 'string')
      return {
        code: input.pattern,
        secondary: typeof input.path === 'string' ? `in ${input.path}` : undefined,
      };
    if (typeof input.glob_pattern === 'string')
      return {
        code: input.glob_pattern,
        secondary:
          typeof input.target_directory === 'string' ? `in ${input.target_directory}` : undefined,
      };
    if (typeof input.search_term === 'string')
      return {
        code: input.search_term,
        secondary: typeof input.explanation === 'string' ? input.explanation : undefined,
      };
    if (typeof input.query === 'string') return { code: input.query };
    // The arguments are the primary record of which file a call acted on. The
    // `locations` fallback below is not a substitute: the Claude reader leaves
    // it empty on every row, and an ACP agent sends it on exactly one mid-call
    // update — so a row that has to fall through to it can end up naming no
    // file at all.
    const filePath = pathArgument(input);
    if (filePath) return { code: filePath, isPath: true };
    if (typeof input.url === 'string') return { code: input.url };
    if (typeof input.description === 'string') return { code: input.description };
  }
  if (block.locations.length > 0) return { code: block.locations[0].path, isPath: true };
  // A diff names the file it changed, and it arrives on the completion — later
  // than the arguments, and from an agent that may have sent none (see
  // {@link ../acp-title.ts}). Only when every diff on the row names the same
  // file: one Edit call can carry several, and picking the first would assert a
  // single subject the call did not have. Those rows keep the `+N -M` chip and
  // the diffs themselves, which tell the truth about how many files there were.
  const diffPaths = new Set(
    block.content.flatMap((c) => (c.type === 'diff' && c.diff?.path ? [c.diff.path] : [])),
  );
  if (diffPaths.size === 1) return { code: [...diffPaths][0], isPath: true };
  return {};
}

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + '…' : str;
}

interface BackgroundInfo {
  taskId: string;
  description?: string;
  summary?: string;
  statusLabel: string;
  /** Kind of background task, e.g. an agent type or `local_bash`. */
  taskType?: string;
  /** Subagent type when the background task is a launched agent. */
  agentType?: string;
  /** Model the background agent runs, when present on the tool input. */
  model?: string;
  /** The command (background bash) or prompt (agent) driving the task. */
  input?: { label: string; text: string };
  /** Human-readable elapsed time once the task has finished. */
  durationLabel?: string;
}

function readString(
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const value = source?.[key];
  return typeof value === 'string' && value ? value : undefined;
}

function getBackgroundInfo(block: ToolCallBlock, statusLabel: string): BackgroundInfo | null {
  const taskId = block.extra?.backgroundTaskId;
  if (typeof taskId !== 'string' || !taskId) return null;
  const raw = block.rawInput ?? null;
  // Unwrap here too: the row above already shows the inner command, and the
  // detail panel's copy button should hand back the same text the row's does.
  const rawCommand = readString(raw, 'command');
  const command = rawCommand ? unwrapShellCommand(rawCommand) : undefined;
  const prompt = readString(raw, 'prompt');
  const input = command
    ? { label: 'Command', text: command }
    : prompt
      ? { label: 'Prompt', text: prompt }
      : undefined;
  return {
    taskId,
    description: readString(block.extra, 'backgroundDescription'),
    summary: readString(block.extra, 'backgroundSummary'),
    statusLabel,
    taskType: readString(block.extra, 'backgroundTaskType'),
    agentType: readString(raw, 'subagent_type') ?? readString(raw, 'agentType'),
    model: readString(raw, 'model'),
    input,
    durationLabel:
      typeof block.duration === 'number' && block.duration > 0
        ? formatDuration(block.duration * 1000)
        : undefined,
  };
}

const BG_STATUS_STYLE: Record<string, { dot: string; text: string }> = {
  running: { dot: 'bg-status-info', text: 'text-status-info' },
  completed: { dot: 'bg-status-success', text: 'text-status-success' },
  failed: { dot: 'bg-status-error', text: 'text-status-error' },
  interrupted: { dot: 'bg-muted-foreground', text: 'text-muted-foreground' },
};

function MetaBadge({ children }: { children: React.ReactNode }) {
  return (
    <Badge variant="secondary" className="gap-1 px-1.5 py-0 text-[10px] font-normal">
      {children}
    </Badge>
  );
}

/** Status + type + model + duration chips, shared by both surfaces. */
function BackgroundMeta({ info }: { info: BackgroundInfo }) {
  const style = BG_STATUS_STYLE[info.statusLabel] ?? BG_STATUS_STYLE.running;
  const typeLabel = info.agentType ?? info.taskType?.replace(/_/g, ' ');
  return (
    <>
      <Badge variant="outline" className={cn('gap-1 px-1.5 py-0 text-[10px]', style.text)}>
        <span className={cn('h-1.5 w-1.5 rounded-full', style.dot)} />
        {info.statusLabel}
      </Badge>
      {typeLabel ? <MetaBadge>{typeLabel}</MetaBadge> : null}
      {info.model ? <MetaBadge>{info.model}</MetaBadge> : null}
      {info.durationLabel ? (
        <MetaBadge>
          <Clock className="h-2.5 w-2.5" />
          {info.durationLabel}
        </MetaBadge>
      ) : null}
    </>
  );
}

/**
 * Compact preview shown in the icon hover popover. Uses the app-wide
 * {@link ResourcePreviewCard} shell so it reads identically to the sandbox,
 * agent, and other resource previews. Stays glanceable — status conveyed by the
 * chip (not repeated in the subtitle), summary only when present.
 */
function BackgroundTaskPopover({ info, icon }: { info: BackgroundInfo; icon: React.ElementType }) {
  // `getIcon` is typed as ElementType (its table allows intrinsic tags), but in
  // practice every entry is a real icon component, so it satisfies the card's
  // ComponentType slot.
  const IconComponent = icon as React.ComponentType<{ className?: string }>;
  return (
    <ResourcePreviewCard
      kindLabel="Background task"
      icon={IconComponent}
      name={info.description ?? 'Background task'}
      id={info.taskId}
      meta={<BackgroundMeta info={info} />}
      description={info.summary ? <p className="text-foreground/80">{info.summary}</p> : undefined}
    />
  );
}

/**
 * Full inline detail shown when the row is expanded. Purpose-built for a task
 * (not an entity preview): it doesn't repeat the row's title, leads with the
 * metadata chips, shows the driving prompt/command in the same copyable block
 * this file uses for tool output, gives the result prominence, and demotes the
 * internal task id to a muted, copyable footer.
 */
function BackgroundTaskDetail({ info }: { info: BackgroundInfo }) {
  return (
    <div className="space-y-2.5" data-testid="background-task-detail">
      <div className="flex flex-wrap items-center gap-1.5">
        <BackgroundMeta info={info} />
      </div>

      {info.input ? <DetailPanel label={info.input.label} text={info.input.text} /> : null}

      {info.summary ? (
        <div className="space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/60">
            Result
          </p>
          <p className="text-xs leading-relaxed whitespace-pre-wrap text-foreground/80">
            {info.summary}
          </p>
        </div>
      ) : null}

      <p className="select-all font-mono text-3xs text-muted-foreground/40" title={info.taskId}>
        {info.taskId}
      </p>
    </div>
  );
}

/**
 * One block of text an expanded row wants to show verbatim — the command a
 * rewritten row came from, the prompt driving a background task, the args a
 * skill was invoked with. A labelled, copyable header strip over a monospace
 * surface, so every such block reads the same wherever it appears.
 */
function DetailPanel({
  label,
  icon: Icon,
  text,
  testId,
}: {
  label: string;
  icon?: React.ElementType;
  /** The panel body, and what its copy button hands back. */
  text: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="overflow-hidden rounded-lg border border-border/20 bg-muted/10"
    >
      <div className="flex items-center gap-2 border-b border-border/20 bg-muted/15 px-3 py-1.5">
        {Icon ? <Icon className="h-3 w-3 shrink-0 text-muted-foreground/60" /> : null}
        <span className="flex-1 truncate text-xs text-muted-foreground/60">{label}</span>
        <CopyButton text={text} />
      </div>
      <pre className="max-h-48 overflow-auto p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground/60">
        {text}
      </pre>
    </div>
  );
}

/**
 * The command behind a rewritten row, as a compact card: a header strip
 * (terminal glyph, label, the interpreter it was demoted from, copy action)
 * over a syntax-highlighted code surface. Shared by the row's overflow tooltip
 * and the `shell` badge's hover panel so "what actually ran" reads identically
 * wherever it surfaces.
 */
function ShellCommandCard({
  command,
  interpreter,
  lang,
}: {
  command: string;
  interpreter?: string;
  /** Shiki language id; omitted for text that isn't code (a path, a query). */
  lang?: string;
}) {
  return (
    <>
      <div className="flex items-center gap-1.5 border-b border-border/40 bg-muted/40 py-1 pl-2.5 pr-1">
        <SquareTerminal className="h-3 w-3 shrink-0 text-muted-foreground/70" />
        {/* The sans label and the mono interpreter have different font metrics,
            so share a baseline rather than centering their line boxes —
            centering leaves the glyphs visibly offset. The icon and copy button
            stay centered on the row. */}
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
          <span className="shrink-0 text-3xs font-medium uppercase tracking-wide text-muted-foreground/70">
            Command
          </span>
          {/* The row drops the interpreter to spend its width on the command;
              name it here so how the command was invoked is demoted rather than
              lost. */}
          {interpreter ? (
            <span
              data-testid="tool-command-interpreter"
              className="min-w-0 truncate font-mono text-3xs text-muted-foreground"
            >
              via {interpreter}
            </span>
          ) : null}
        </span>
        {/* Stop the copy click bubbling to the row's expand toggle: the panel is
            portaled but stays a React descendant of the row's onClick. */}
        <span onClick={(e) => e.stopPropagation()}>
          <CopyIconButton
            text={command}
            testId="tool-command-copy"
            ariaLabel="Copy command"
            className="h-6 w-6 text-muted-foreground hover:bg-muted hover:text-foreground"
          />
        </span>
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all px-3 py-2.5 font-mono text-xs leading-relaxed text-foreground/85">
        {lang ? <InlineHighlightedCode code={command} lang={lang} /> : command}
      </pre>
    </>
  );
}

/**
 * The `shell` chip on a row whose headline was rewritten from a command. It is
 * both a disclaimer and an affordance: it says this row is our words rather
 * than a tool's, and hovering it shows the exact command those words came from,
 * so the rewrite is never the only thing a reader can see.
 */
/**
 * A chip on the tool row that opens a panel on hover. The row is dense, so
 * every one of these reads the same: muted, tiny, and marked with a help
 * cursor because the panel is where the detail lives.
 */
const ROW_HOVER_BADGE_CLASS =
  'shrink-0 cursor-help rounded bg-muted px-1 py-0.5 text-3xs text-muted-foreground transition-colors hover:text-foreground';

function ShellOriginBadge({
  command,
  interpreter,
  testId,
}: {
  command: string;
  interpreter?: string;
  testId: string;
}) {
  return (
    <HoverCard openDelay={120} closeDelay={100}>
      <HoverCardTrigger asChild>
        {/* Opening the panel shouldn't also toggle the row's expansion. */}
        <span
          role="button"
          tabIndex={0}
          data-testid={testId}
          aria-label="Show the shell command"
          onClick={(e) => e.stopPropagation()}
          className={ROW_HOVER_BADGE_CLASS}
        >
          shell
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-[34rem] max-w-[90vw] overflow-hidden p-0"
        aria-label="Shell command"
      >
        <ShellCommandCard command={command} interpreter={interpreter} lang="bash" />
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * A chip that clips its own text and still sits on the row's baseline.
 *
 * A chip is a `<button>`, an atomic inline box that `text-overflow: ellipsis`
 * never reaches into, so it has to clip its own text or a long path clips with
 * no ellipsis at all. The clipping is not what moves it, and this is the part
 * worth writing down, because guessing it wrong costs the row a magic offset:
 * a `<button>` keeps the baseline of its own text even as a scroll container
 * (measured at 0 in Chromium and Firefox — a bare `inline-block` really does
 * lose it, which is where the assumption comes from). `vertical-align` is the
 * whole story. `bottom` lines the chip's box up with the bottom of the line
 * box, which leaves its text riding above the label beside it by its own
 * vertical padding — 2px at the row's `text-xs`. `baseline`, the default the
 * chip should never have left, is what puts it on the row's.
 */
const CLIPPED_CHIP_CLASSES = 'inline-block max-w-full min-w-0 truncate align-baseline';

/**
 * {@link CLIPPED_CHIP_CLASSES} for a chip a scanner plugin owns the markup of:
 * the row can only reach it as a descendant. Spelled out rather than derived
 * from the constant above because Tailwind generates utilities by scanning the
 * source for whole class names, so a computed variant would never be built.
 */
const CLIPPED_CHIP_DESCENDANT_CLASSES =
  '[&_button]:inline-block [&_button]:max-w-full [&_button]:min-w-0 [&_button]:truncate [&_button]:align-baseline';

/**
 * The time limit a `timeout …` prefix put on a command, as a glyph.
 *
 * `timeout 240` is a guard rail the agent set, not the work it did, and it
 * occupies exactly the leading characters of the row a reader scans first. So
 * the row spends its width on the command and says "this one is on a clock"
 * with a stopwatch; the number — the part that's only occasionally interesting
 * — is one hover away.
 */
function CommandTimeoutBadge({ timeout }: { timeout: ShellTimeout }) {
  // Say which command is on the clock when it isn't the whole row: `timeout`
  // guards exactly one command, and a chip that let a reader assume otherwise
  // would be promising a limit on work that ran without one.
  const label = timeout.wholeCommand
    ? `Times out after ${timeout.label}`
    : `First command times out after ${timeout.label}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Reading the limit shouldn't also toggle the row's expansion. */}
        <span
          role="button"
          tabIndex={0}
          data-testid="tool-timeout"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 cursor-help self-center text-muted-foreground/50 transition-colors hover:text-foreground"
        >
          <Timer className="h-3 w-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Marks a write that only went through on the second try, because the harness
 * refused the first one until the file had been read — or read again, after it
 * changed ({@link ../read-before-write.ts}). The refused attempt is gone from the
 * transcript, so this badge is the only place that says it happened, and its
 * panel carries the message that caused it.
 */
function ReadFirstRetryBadge({ retry }: { retry: ReadBeforeWriteRetry }) {
  return (
    <HoverCard openDelay={120} closeDelay={100}>
      <HoverCardTrigger asChild>
        {/* Opening the panel shouldn't also toggle the row's expansion. */}
        <span
          role="button"
          tabIndex={0}
          data-testid="read-first-retry-badge"
          aria-label="Why this write was retried"
          onClick={(e) => e.stopPropagation()}
          className={ROW_HOVER_BADGE_CLASS}
        >
          retried
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 p-3" aria-label="Retried after read">
        <div className="space-y-2 text-xs">
          <p className="text-muted-foreground">
            The first write was refused by the harness&rsquo;s read-first rule. The agent read{' '}
            <code className="font-mono text-foreground/70">{retry.path}</code> and wrote it again.
          </p>
          <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 font-mono text-3xs leading-relaxed text-muted-foreground/80">
            {retry.message}
          </pre>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * How a summarized row renders its subject. The kind is a display hint, not a
 * claim: a pattern is quoted because that is how it was written, a git ref gets
 * the one accent on the row because a branch name is the thing a reader scans
 * for, and everything else stays muted mono so the verb still leads.
 */
const SUBJECT_CLASSES: Record<ShellSubject['kind'], string> = {
  pattern: 'rounded bg-muted/40 px-1.5 py-0.5 text-foreground/70',
  path: 'text-foreground/50',
  ref: 'rounded bg-status-accent-teal/10 px-1.5 py-0.5 text-status-accent-teal',
  // A gist is the ref chip's sibling — another GitHub thing the row can open —
  // so it wears the neighbouring accent: same family, plainly not a branch.
  gist: 'rounded bg-status-accent-cyan/10 px-1.5 py-0.5 text-status-accent-cyan',
  text: 'text-foreground/60',
  // A line of a program the command was handed, chipped like a pattern so it
  // reads as quoted-from-source rather than as prose the row wrote.
  code: 'rounded bg-muted/40 px-1.5 py-0.5 text-foreground/70',
};

/** The repository a row's git refs live in, resolved to a web link. */
interface GitRepoLink {
  /** `owner/repo`, as the popover header shows it. */
  slug: string;
  /** The repo's web URL on the deployment's GitHub host. */
  url: string;
}

/**
 * The repo the agent works in, from its `git-repo` attachment and the
 * deployment's GitHub host. Null while the agent is loading or when no repo is
 * attached — the ref popover then keeps its copy affordance and offers no
 * links, rather than guessing at a host.
 *
 * Same key (and cache entry) as `useAgent`, but `select` narrows the
 * subscription to the repo slug: agent updates stream in over the socket for
 * as long as a run lasts, and a transcript of rows shouldn't re-render for
 * every one of them when the slug never changes mid-run.
 */
function useAgentRepoLink(agentId?: string): GitRepoLink | null {
  const base = useGitHubWebBaseUrl();
  const orgKey = useOrgKey();
  const { data: slug } = useQuery({
    queryKey: queryKeys.agent(orgKey, agentId ?? ''),
    queryFn: () => apiGetAgent(agentId ?? ''),
    enabled: Boolean(agentId),
    select: (agent) => getGitRepoAttachment(agent)?.repoSlug?.trim() || undefined,
  });
  return slug ? { slug, url: `${base.replace(/\/+$/, '')}/${slug}` } : null;
}

const REF_KIND_ICONS: Record<GitRefKind, React.ElementType> = {
  branch: GitBranch,
  commit: GitCommitHorizontal,
  tag: Tag,
  pull: GitPullRequest,
};

const REF_KIND_LABELS: Record<GitRefKind, string> = {
  branch: 'branch',
  commit: 'commit',
  tag: 'tag',
  pull: 'pull request',
};

/**
 * The header strip both entity popovers wear: a glyph, what kind of thing the
 * panel is about, and — pushed to the right — where it lives (the repository for
 * a git ref, the owner for a gist). One component so the two can't drift.
 */
function EntityPopoverHeader({
  icon: Icon,
  label,
  trailing,
}: {
  icon: React.ElementType;
  label: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 border-b border-border/40 bg-muted/40 px-3 py-1.5">
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground/70" />
      <span className="shrink-0 text-3xs font-medium uppercase tracking-wide text-muted-foreground/70">
        {label}
      </span>
      {trailing ? <span className="ml-auto flex min-w-0 justify-end">{trailing}</span> : null}
    </div>
  );
}

/**
 * One side of a range, as the same teal chip the row already taught the
 * reader to click: a branch pill that opens the branch on GitHub.
 */
function GitRefPill({ link, repo }: { link: GitRefLink; repo: GitRepoLink | null }) {
  const chip = 'min-w-0 truncate rounded bg-status-accent-teal/10 px-1.5 py-0.5 font-mono text-xs';
  if (!repo) {
    return <span className={cn(chip, 'text-status-accent-teal')}>{link.label}</span>;
  }
  return (
    <a
      href={`${repo.url}/${link.refPath}`}
      target="_blank"
      rel="noopener noreferrer"
      title={`View ${REF_KIND_LABELS[link.kind]} on GitHub`}
      className={cn(
        chip,
        'text-status-accent-teal transition-colors hover:bg-status-accent-teal/20 hover:underline',
      )}
    >
      {link.label}
    </a>
  );
}

/**
 * A ref chip that opens into what it names. The chip clips a long branch name
 * and says nothing about where it lives; clicking it lists every ref in full —
 * copyable, and linked to the repo's GitHub host when the agent has one
 * attached — so "which branch was that" is one click instead of a trip to the
 * expanded command. A range (`main..feat/x`) renders the way every GitHub
 * user already reads it — `main → feat/x` — with the compare view one click
 * below, since that page is what a reader usually wanted from a diff row.
 */
function GitRefPopover({
  text,
  parsed,
  repo,
}: {
  /** The chip's text, shown unchanged as the trigger. */
  text: string;
  parsed: GitRefSubject;
  repo: GitRepoLink | null;
}) {
  // The range owns its two refs; anything else in the subject keeps a list row.
  const rangePaths = parsed.range
    ? new Set([parsed.range.from.refPath, parsed.range.to.refPath])
    : null;
  const listRefs = rangePaths ? parsed.refs.filter((r) => !rangePaths.has(r.refPath)) : parsed.refs;
  const HeaderIcon = parsed.range ? GitCompare : GitBranch;
  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* Opening the popover shouldn't also toggle the row's expansion. The
            chip sits inside a truncating span, and an ellipsis never crosses
            into a nested button — so the button is its own clipping container
            (see CLIPPED_CHIP_CLASSES), same as the file-preview chips. */}
        <button
          type="button"
          data-testid="shell-ref-chip"
          aria-label={`Git refs: ${text}`}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            CLIPPED_CHIP_CLASSES,
            'cursor-pointer text-left font-mono outline-none transition-colors',
            'hover:bg-status-accent-teal/20 focus-visible:ring-1 focus-visible:ring-status-accent-teal/60',
            SUBJECT_CLASSES.ref,
          )}
        >
          {text}
        </button>
      </PopoverTrigger>
      {/* The panel is portaled but stays a React descendant of the row's
          onClick, so clicks inside it must not bubble into the expand toggle.
          Auto-focus is suppressed so the panel opens showing its content, not
          a focus ring on the first button; everything stays one Tab away. */}
      <PopoverContent
        align="start"
        onClick={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto min-w-72 max-w-96 overflow-hidden p-0"
        aria-label="Git refs"
      >
        <EntityPopoverHeader
          icon={HeaderIcon}
          label={parsed.range ? 'Compare' : parsed.refs.length === 1 ? 'Git ref' : 'Git refs'}
          // The same repo badge the agent header wears, so "which repo do these
          // links open" reads identically everywhere — and is itself a link to
          // the repository.
          trailing={
            repo ? <AgentRepoChip repoSlug={repo.slug} href={repo.url} variant="badge" /> : null
          }
        />
        {parsed.range ? (
          // A diff range, laid out the way its GitHub page reads: base on the
          // left, the branch under comparison on the right, each pill a link
          // to its branch and the compare view itself one row below.
          <div
            data-testid="shell-ref-range"
            className="flex min-w-0 items-center gap-2 px-3 py-2.5"
          >
            <GitRefPill link={parsed.range.from} repo={repo} />
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
            <GitRefPill link={parsed.range.to} repo={repo} />
          </div>
        ) : null}
        {listRefs.length > 0 ? (
          <ul className="divide-y divide-border/20" data-testid="shell-ref-popover-list">
            {listRefs.map((link) => {
              const RefIcon = REF_KIND_ICONS[link.kind];
              const href = repo ? `${repo.url}/${link.refPath}` : null;
              return (
                <li key={link.refPath} className="flex items-start gap-2.5 px-3 py-2">
                  <RefIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-accent-teal/80" />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    {/* Lead with the name a reader knows — the branch, not its
                      refs/heads/ spelling — styled as the link it is. */}
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`View ${REF_KIND_LABELS[link.kind]} on GitHub`}
                        className="group/ref inline-flex min-w-0 items-baseline gap-1.5 self-start"
                      >
                        <span className="min-w-0 break-all font-mono text-xs font-medium text-status-accent-teal group-hover/ref:underline">
                          {link.label}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0 self-center text-status-accent-teal/60 transition-colors group-hover/ref:text-status-accent-teal" />
                      </a>
                    ) : (
                      <span className="break-all font-mono text-xs font-medium text-foreground/85">
                        {link.label}
                      </span>
                    )}
                    {/* The plumbing spelling stays available, demoted: it is
                      what the command actually said, and what copy hands back. */}
                    <span className="break-all text-3xs text-muted-foreground/60">
                      {REF_KIND_LABELS[link.kind]}
                      {link.ref !== link.label ? (
                        <>
                          {' · '}
                          <span className="font-mono">{link.ref}</span>
                        </>
                      ) : null}
                    </span>
                  </span>
                  <CopyIconButton
                    text={link.ref}
                    ariaLabel={`Copy ${link.ref}`}
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                  />
                </li>
              );
            })}
          </ul>
        ) : null}
        {repo && parsed.comparePath ? (
          <a
            href={`${repo.url}/${parsed.comparePath}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shell-ref-compare"
            className="group/cmp flex items-center gap-1.5 border-t border-border/40 px-3 py-2 text-xs font-medium text-status-accent-teal transition-colors hover:bg-status-accent-teal/10"
          >
            <GitCompare className="h-3 w-3 shrink-0" />
            Compare on GitHub
            <ExternalLink className="ml-auto h-3 w-3 shrink-0 text-status-accent-teal/60 transition-colors group-hover/cmp:text-status-accent-teal" />
          </a>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A gist chip that opens into the gist it names. The row shows the operand as
 * the command wrote it — a 32-character id, or a URL that eats the row — which
 * says nothing a reader can act on; clicking it gives the id in full, copyable,
 * and a link to the gist on the deployment's GitHub host. Unlike a git ref, a
 * gist hangs off no repository, so the link never depends on the agent having
 * one attached.
 */
function GistPopover({ text, gist }: { text: string; gist: GistRef }) {
  const base = useGitHubWebBaseUrl();
  const href = gistWebUrl(base, gist);
  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* Same clipping and click-isolation contract as the ref chip: opening
            the popover must not also toggle the row's expansion, and a button
            has to clip its own text (see CLIPPED_CHIP_CLASSES). */}
        <button
          type="button"
          data-testid="shell-gist-chip"
          aria-label={`Gist: ${text}`}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            CLIPPED_CHIP_CLASSES,
            'cursor-pointer text-left font-mono outline-none transition-colors',
            'hover:bg-status-accent-cyan/20 focus-visible:ring-1 focus-visible:ring-status-accent-cyan/60',
            SUBJECT_CLASSES.gist,
          )}
        >
          {text}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onClick={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto min-w-72 max-w-96 overflow-hidden p-0"
        aria-label="Gist"
        data-testid="shell-gist-popover"
      >
        <EntityPopoverHeader
          icon={CodeXml}
          label="Gist"
          // Who owns it, when the command said so — the gist's answer to the
          // repo badge, and a link to their profile.
          trailing={
            gist.owner ? (
              <a
                href={gistOwnerUrl(base, gist.owner)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shell-gist-owner"
                className="min-w-0 truncate font-mono text-3xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
              >
                {gist.owner}
              </a>
            ) : null
          }
        />
        <div className="flex items-start gap-2.5 px-3 py-2">
          <CodeXml className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-accent-cyan/80" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title="View gist on GitHub"
              data-testid="shell-gist-link"
              className="group/gist inline-flex min-w-0 items-baseline gap-1.5 self-start"
            >
              <span className="min-w-0 break-all font-mono text-xs font-medium text-status-accent-cyan group-hover/gist:underline">
                {gist.id}
              </span>
              <ExternalLink className="h-3 w-3 shrink-0 self-center text-status-accent-cyan/60 transition-colors group-hover/gist:text-status-accent-cyan" />
            </a>
            <span className="text-3xs text-muted-foreground/60">gist</span>
          </span>
          <CopyIconButton
            text={gist.id}
            ariaLabel={`Copy ${gist.id}`}
            className="h-6 w-6 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * A path rendered as the file-preview affordance, on a row that is itself
 * clickable. Two things have to be handled wherever that happens, so they are
 * handled once here: opening the preview must not also toggle the row, and the
 * scanner's markup is not ours to change — it wraps the path in a `<button>`,
 * which the row has to turn into its own clipping container
 * ({@link CLIPPED_CHIP_DESCENDANT_CLASSES}) for the leading ellipsis to
 * survive.
 */
function PreviewPathChip({
  path,
  label,
  className,
  dir,
}: {
  path: string;
  label: React.ReactNode;
  className: string;
  /** `rtl` keeps the ellipsis on the leading edge, so the basename survives. */
  dir?: 'rtl';
}) {
  return (
    <span
      dir={dir}
      className={cn(className, CLIPPED_CHIP_DESCENDANT_CLASSES)}
      onClick={(e) => e.stopPropagation()}
    >
      <ScannedPath path={path} label={label} />
    </span>
  );
}

/**
 * One side of a subject. A `path` subject that names a single file or directory
 * gets the same preview affordance a Read row's path has: the text is the
 * operand as the command wrote it, so making it openable adds an action rather
 * than a claim. A phrase standing in for several operands ("a.ts and b.ts",
 * "3 paths") is not a path and stays text — see `absoluteFilePath`.
 */
function ShellSubjectText({ kind, text }: { kind: ShellSubject['kind']; text: string }) {
  const workingDir = useWorkingDir();
  const previewPath = kind === 'path' ? absoluteFilePath(text, workingDir) : null;
  const quote = kind === 'pattern' ? '"' : '';
  const className = cn('truncate font-mono', SUBJECT_CLASSES[kind]);
  if (previewPath) return <PreviewPathChip path={previewPath} label={text} className={className} />;
  return (
    <span className={className}>
      {quote}
      {text}
      {quote}
    </span>
  );
}

function ShellSubjectView({ subject, repo }: { subject: ShellSubject; repo?: GitRepoLink | null }) {
  // A ref chip becomes an affordance when its text names something a repo host
  // can show. Placeholder ref subjects ("3 paths", "the upstream branch") and
  // two-sided ones keep the plain rendering.
  if (subject.kind === 'ref' && !subject.to) {
    const parsed = parseGitRefSubject(subject.text);
    if (parsed.refs.length > 0) {
      return <GitRefPopover text={subject.text} parsed={parsed} repo={repo ?? null} />;
    }
  }
  // Same rule for a gist: the chip opens only when the text's shape names one.
  if (subject.kind === 'gist' && !subject.to) {
    const gist = parseGistSubject(subject.text);
    if (gist) return <GistPopover text={subject.text} gist={gist} />;
  }
  const body = <ShellSubjectText kind={subject.kind} text={subject.text} />;
  // A two-sided action (`mv a b`, `s/old/new/`) is one subject, not two: the
  // arrow carries the relationship, so both sides stay in the same slot.
  if (!subject.to) return body;
  return (
    <span className="flex min-w-0 items-baseline gap-1 truncate">
      {body}
      <span className="shrink-0 text-muted-foreground/50">→</span>
      <ShellSubjectText kind={subject.kind} text={subject.to} />
    </span>
  );
}

/**
 * The headline of a summarized row: what the command acted on, then the
 * qualifiers. Details shrink three times faster than the subject, so a narrow
 * row gives up "in web/src · *.ts" before it gives up the pattern that makes
 * the row worth reading.
 */
function ShellHeadlineView({ step, repo }: { step: ShellStep; repo?: GitRepoLink | null }) {
  return (
    <>
      {step.subject ? (
        <span className="min-w-0 shrink truncate" data-testid="shell-summary-subject">
          <ShellSubjectView subject={step.subject} repo={repo} />
        </span>
      ) : null}
      {step.details.length > 0 ? (
        <span
          data-testid="shell-summary-details"
          className="min-w-0 shrink-[3] truncate text-muted-foreground/50"
        >
          {step.details.join(' · ')}
        </span>
      ) : null}
    </>
  );
}

/**
 * The chain behind a multi-command row. The headline can only describe the
 * first command, so the expanded body accounts for every one of them: each step
 * reads as its own sentence with the exact text it came from underneath, in the
 * order they ran.
 */
function ShellStepsPanel({ steps, repo }: { steps: ShellStep[]; repo?: GitRepoLink | null }) {
  return (
    <div
      data-testid="shell-summary-steps"
      className="overflow-hidden rounded-lg border border-border/20 bg-muted/10"
    >
      <div className="flex items-center gap-2 border-b border-border/20 bg-muted/15 px-3 py-1.5">
        <span className="flex-1 truncate text-xs text-muted-foreground/60">
          {steps.length} commands
        </span>
      </div>
      <ol className="divide-y divide-border/10">
        {steps.map((step, i) => {
          const StepIcon = SHELL_SUMMARY_ICONS[step.icon];
          return (
            <li key={i} className="flex items-baseline gap-2 px-3 py-1.5 text-xs">
              <span className="w-3 shrink-0 self-center text-muted-foreground/40">
                <StepIcon className="h-3 w-3" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                {step.recognized ? (
                  <span className="flex min-w-0 items-baseline gap-1.5 text-muted-foreground">
                    <span className="shrink-0">{step.words.done}</span>
                    <ShellHeadlineView step={step} repo={repo} />
                  </span>
                ) : null}
                <code className="min-w-0 truncate font-mono text-3xs text-foreground/45">
                  {step.raw}
                </code>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function ToolCallBlockView({
  block,
  agentId,
  defaultExpanded = false,
}: {
  block: ToolCallBlock;
  agentId?: string;
  /** Start expanded. Used by gallery/story surfaces to show the open state. */
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { toolCallRenderers } = usePlugins();
  // Where this row's git refs can be viewed. One cached query per agent, shared
  // by every row; null until the agent (with its git-repo attachment) resolves.
  const repo = useAgentRepoLink(agentId);
  // Where the session runs, so a path written relative to it can still be
  // previewed. Null outside a chat session, which keeps such paths plain.
  const sessionWorkingDir = useWorkingDir();

  // Plugin-contributed renderer for this tool (e.g. the workstation plugin's
  // terminal-style display for `workstation_run_command`). Keyed by the
  // normalized name so `mcp__workstation__workstation_run_command` and the
  // bare `workstation_run_command` resolve identically. The header row stays
  // host-owned; the renderer replaces the expanded body.
  const rawToolName =
    typeof block.extra?.toolName === 'string' && block.extra.toolName.length > 0
      ? block.extra.toolName
      : block.title;
  const toolName = normalizeToolName(rawToolName);
  const customRenderer = toolCallRenderers.get(toolName);
  // An MCP tool call arrives as `mcp__<server>__<tool>` — parsed here so the
  // row can read `<server>:<tool>` with its actual arguments, instead of
  // either the raw double-underscored name or (worse) a verb guessed from
  // `inferToolKind`'s keyword heuristic, which rarely fits an arbitrary MCP
  // tool schema (e.g. "start_agent" heuristically matches "think"). A
  // plugin-contributed renderer already owns the row's identity, so it wins.
  // Memoized so its object identity is stable across renders — the arg
  // stringification memos below key off it.
  const mcpName = useMemo(
    () => (customRenderer ? null : parseMcpToolName(rawToolName)),
    [customRenderer, rawToolName],
  );

  const input = block.rawInput ?? null;
  // A call can name the directory it ran in (Codex reports one per command),
  // which beats the session's: an agent working in a subdirectory writes its
  // paths relative to that one.
  const rowWorkingDir = readString(input, 'cwd');
  const workingDir = rowWorkingDir ?? sessionWorkingDir;
  // Some shell commands are exact stand-ins for the native tools: a quoted
  // heredoc (`cat > f <<'EOF'`) writes bytes we can already see, `echo x >> f`
  // adds a line, and `cat f` just prints the file. Those rows adopt the
  // Read/Write presentation — file icon, "Read"/"Wrote"/"Appended to" verb,
  // previewable path, diff or styled read card — with the command itself moved
  // into the expanded body. A plugin renderer or a background task owns its own
  // presentation, so both opt out, and so does an MCP call: its identity is the
  // `server:tool` it names, which a Read/Write reinterpretation would hide.
  const isBackgroundTask = typeof block.extra?.backgroundTaskId === 'string';
  // One switch governs every shell *rewrite* on the row (the toolbar's "Human
  // readable commands"). Turned off, a shell row shows the command it ran and
  // nothing else — which also means the bash parser is never fetched. It does
  // not govern how that command is presented: the interpreter and the timeout
  // prefix are demoted to hover either way, since neither is the agent's words
  // being replaced by ours.
  const [humanReadable] = useHumanReadableCommands();
  const canBeShellFileOp =
    humanReadable && !customRenderer && !mcpName && !isBackgroundTask && block.kind === 'execute';
  // The bash parser is fetched on demand; until it lands the row renders as a
  // plain command and re-renders once it resolves.
  const shellSyntax = useShellParser(canBeShellFileOp);
  // Parsing walks the whole command, heredoc body included; a streaming row
  // re-renders on every chunk, so keep it off the render path.
  const shellFileOp = useMemo(
    () => (shellSyntax ? shellFileOpFromInput(input, shellSyntax) : null),
    [shellSyntax, input],
  );
  // Everything else a shell command can be: a search, a git operation, a
  // package script. Those rows keep their `execute` kind — only the words, icon
  // and subject change — so nothing downstream mistakes them for a file op.
  const shellSummary = useMemo(
    () => (shellSyntax && !shellFileOp ? shellSummaryFromInput(input, shellSyntax) : null),
    [shellSyntax, shellFileOp, input],
  );
  // Everything downstream reads the native tool's title and kind off `view`, so
  // the shell row travels the same code paths as a real Read/Write call.
  const view: ToolCallBlock = shellFileOp
    ? {
        ...block,
        // `Append` is not a real tool name anywhere; it is a title this file's
        // own prefix/icon tables answer to, so an append travels the same
        // edit-kind code path as a write while still reading as what it did.
        title:
          shellFileOp.op === 'read' ? 'Read' : shellFileOp.op === 'append' ? 'Append' : 'Write',
        kind: shellFileOp.op === 'read' ? 'read' : 'edit',
      }
    : block;
  // Both rewrites are only reached when the input carried a command string, and
  // both move it off the header row, so the body must be able to show it again.
  const shellCommand = shellFileOp || shellSummary ? (input?.command as string) : null;
  // A rewritten row shows neither the command nor the interpreter it ran under,
  // so the badge's panel credits both. `display.interpreter` only covers rows
  // that still show a command, which these no longer do.
  const shellDisplay = shellCommand ? formatShellCommand(shellCommand) : undefined;
  const shellInterpreter = shellDisplay?.interpreter;

  const Icon =
    customRenderer?.icon ??
    (mcpName
      ? Wrench
      : shellSummary
        ? SHELL_SUMMARY_ICONS[shellSummary.headline.icon]
        : getIcon(view));
  // Set only on a write whose refused first attempt the turn erased.
  const readFirstRetry = readBeforeWriteRetryOf(block);
  // A skipped/dismissed AskUserQuestion comes back as a denied tool_result
  // (is_error), which would otherwise render as a red "failed" tool line with
  // the deny reason as error output. The question's own card already shows the
  // outcome, so don't surface a failure here.
  const isAskUserQuestion = block.title === 'AskUserQuestion';
  // A search with no matches or `gh pr checks` with failing checks exits
  // non-zero, but the command did what it was asked — the exit code reports
  // the outcome it observed, not a failure of the command itself. Treat these
  // as completed so the row reads "Searched for X" / "Checked pull request
  // checks" instead of "Couldn't search for X" / "Couldn't check".
  const exitReportsOutcome = shellSummary?.headline.exitReportsOutcome ?? false;
  const isFailed = block.status === 'failed' && !isAskUserQuestion && !exitReportsOutcome;
  const isInProgress = block.status === 'in_progress' || block.status === 'pending';
  // A cancelled call is spoken about in the running tense: it was interrupted
  // partway, so the completed words would claim work that never finished.
  const tense: Tense = isFailed
    ? 'failed'
    : isInProgress || block.status === 'cancelled'
      ? 'active'
      : 'done';
  // The file path is what a shell file-op row is really about, so it takes the
  // command's place as the row's `code` — which is also what makes it
  // previewable. Resolved before the row's words, because whether the call
  // named a subject at all decides which words it gets.
  const displayInfoResult = getDisplayInfo(
    view,
    shellFileOp ? { file_path: shellFileOp.path } : input,
  );
  // Nothing on the call named what it acted on — no arguments, no locations, no
  // diff — so our verb would be the entire row ("Read", naming no file). The
  // agent's own title is then the only record of it, and it leads the row in
  // place of the verb it already contains; {@link ../acp-title.ts} owns when a
  // title is that record and when it is just the tool's name. Rows whose
  // identity is not ours to restate keep what they have: a plugin renderer, an
  // MCP call, a summarized shell command.
  const titleLabel =
    !customRenderer && !mcpName && !shellSummary && !displayInfoResult.code
      ? toolCallTitleLabel(view.title)
      : null;
  const { text: prefix, saysFailed: prefixSaysFailed } = customRenderer
    ? {
        text:
          tense === 'failed'
            ? (customRenderer.labelFailed ?? customRenderer.label)
            : tense === 'active'
              ? (customRenderer.labelInProgress ?? customRenderer.label)
              : customRenderer.label,
        saysFailed: tense === 'failed' && customRenderer.labelFailed != null,
      }
    : mcpName
      ? // An MCP row is identified by the `server:tool` it named, which has no
        // tense to speak in; its status stays on the chip.
        { text: `${mcpName.server}:${mcpName.tool}`, saysFailed: false }
      : shellSummary
        ? // The summary carries all three tenses, so a running search reads
          // "Searching for" and a failed one "Couldn't search for", like every
          // other row does.
          {
            text:
              tense === 'failed' && shellSummary.steps.length > 1
                ? // The aggregate status does not identify which step in a
                  // chain failed, so do not blame the summarized first step.
                  'Command failed'
                : shellSummary.headline.words[tense],
            saysFailed: tense === 'failed',
          }
        : titleLabel
          ? // The agent's sentence, in the agent's tense — like an MCP row, the
            // status chip beside it says how the call ended.
            { text: titleLabel, saysFailed: false }
          : getPrefix(view, tense);

  // MCP tools carry an arbitrary, per-server argument shape that
  // `getDisplayInfo`'s single-field heuristics (command/pattern/file_path/…)
  // often don't match, so without a fallback the row would show no arguments
  // at all. But those heuristics DO cover real MCP surface too (a `query` or
  // `path` field is common), and when they hit, their result — a clean path
  // preview, a highlighted command — is strictly more useful than a raw JSON
  // dump of every field. So only fall back to the JSON dump when
  // `getDisplayInfo` found nothing. Note this only governs the compact row's
  // `code` text; whether the full-argument tooltip is reachable at all is a
  // separate concern, gated below by `mcpPreviewSafe`.
  // Memoized for the same reason as `diffCounts` below: MCP args can embed
  // whole file contents, and the surrounding turn re-renders every row on
  // every streamed token.
  const mcpArgsPretty = useMemo(
    () => (mcpName ? formatMcpArgs(input, 2) : undefined),
    [mcpName, input],
  );
  const mcpArgsCompact = useMemo(
    () => (mcpName && !displayInfoResult.code ? formatMcpArgs(input) : undefined),
    [mcpName, displayInfoResult.code, input],
  );
  const display: DisplayInfo =
    mcpArgsCompact !== undefined ? { code: mcpArgsCompact, lang: 'json' } : displayInfoResult;
  // The limit belongs to the whole command, not to the words the row happens to
  // show, so a file-op row — whose `display` is built from the path it resolved
  // to, not from the command — reads it off the command instead.
  const commandTimeout = display.timeout ?? shellDisplay?.timeout;
  // An MCP call that wrote a file is the same event the native `Write` tool
  // reports, so it reads the same way: the path takes the preview chip and the
  // bytes become the diff below, instead of a raw argument dump the reader has
  // to hover. Only a completed, successful call qualifies — an interrupted or
  // failed write has not put those bytes on disk, and its arguments are the
  // only record of what it tried, so those rows keep the full-args tooltip.
  // Memoized so the diff item derived from it below keeps a stable identity:
  // the per-diff caches in `diff-utils` are keyed on that object, and the
  // surrounding turn re-renders every row on every streamed token.
  const mcpWrite = useMemo(
    () =>
      mcpName && view.kind === 'edit' && !isFailed && block.status === 'completed'
        ? writeFileInput(input)
        : null,
    [mcpName, view.kind, isFailed, block.status, input],
  );
  // An MCP tool whose args resolve to a real path (via `getDisplayInfo` above)
  // can take the path-preview affordance below instead of the full-args
  // tooltip — but only when nothing else on the call would go unshown. The
  // path being the whole input is one way to earn that (`read_file({ path })`
  // has nothing else to lose); a recognized write is the other, since the row
  // shows its one other argument as a diff.
  const mcpPreviewSafe =
    !mcpName || input == null || Object.keys(input).length <= 1 || mcpWrite !== null;
  // A path inside the working directory renders relative to it instead of as
  // the devbox-absolute path an agent actually sees — the directory is the
  // same on every row, so spelling it out each time is pure noise. Anything
  // outside it stays absolute: the prefix is the only thing saying so.
  // `previewPath` below intentionally keeps the untouched `display.code` —
  // the content-scanner preview resolves the real path on the devbox, not
  // this display string.
  const displayCode =
    display.code && display.isPath ? relativeToWorkingDir(display.code, workingDir) : display.code;
  // Paths render in full and clip from the start via the dir="rtl" CSS ellipsis
  // on their container, so the informative tail (the basename) always survives
  // behind a single leading "…". Only CSS knows the real pixel width, so it owns
  // path truncation — a fixed JS char cap either over-clips a wide row or, worse,
  // leaves a slice that still overflows a narrow one and gets hard-clipped a
  // second time with no ellipsis. Non-path labels have no meaningful tail, so
  // they keep simple end-truncation.
  const code = displayCode ? (display.isPath ? displayCode : truncate(displayCode, 60)) : undefined;
  // Args are free text and can arrive with line breaks; the row is one line, so
  // collapse the whitespace rather than letting a newline blow out its height.
  // The expanded body keeps the text exactly as it was written.
  const rowArgs = display.args?.replace(/\s+/g, ' ').trim();
  // Hovering is worth it when the row can't tell the whole story: the label is
  // clipped, its line breaks were flattened, an interpreter was stripped, or —
  // for a path shortened against the working directory — the prefix the row no
  // longer spells out. The length test runs on the *displayed* string, so
  // trimming a path below the cap no longer buys it a tooltip on width alone;
  // the shortening itself is what earns one, and the tooltip shows the full
  // absolute path rather than the label a reader can already see.
  const shortenedPath = displayCode !== display.code;
  const fullCode =
    displayCode &&
    (displayCode.length > 60 || shortenedPath || display.expandedCode || display.interpreter)
      ? (display.expandedCode ?? display.code)
      : undefined;
  // dir="rtl" on the truncating element moves the CSS overflow ellipsis to the
  // start; the inner dir="ltr" span isolates the path as a single
  // left-to-right run so its slashes and dots aren't reordered.
  const codeDir = display.isPath ? ('rtl' as const) : undefined;
  const codeLabel = code && display.isPath ? <span dir="ltr">{code}</span> : code;
  // Shell commands (and other highlightable `code`) render through Shiki both
  // in the row and in the overflow tooltip; everything else — paths, patterns,
  // queries — keeps its plain treatment. Highlighting is lazy and falls back to
  // the plain string until it lands, so layout and truncation are unaffected.
  const codeNode =
    code && display.lang ? <InlineHighlightedCode code={code} lang={display.lang} /> : codeLabel;
  // Highlighted commands get a subtle inline-code surface so the Shiki tokens
  // read as a code snippet rather than loose colored text on the page
  // background — matching how the diff/read views always sit highlighted code
  // on a muted card. This especially firms up light mode, where the tokens are
  // otherwise low-contrast against the near-white background. Accent `code` (a
  // Skill's name) gets a tinted violet chip instead, so the capability stands
  // out from the muted row. The text color lives on `codeTextClass` (not here)
  // so it cleanly overrides the base muted color rather than racing it.
  const codeChipClass = display.accent
    ? 'rounded bg-status-accent-violet/10 px-1.5 py-0.5'
    : display.lang
      ? 'rounded bg-muted/40 px-1.5 py-0.5'
      : '';
  const codeTextClass = display.accent ? 'text-status-accent-violet' : 'text-foreground/50';
  // Read and edit rows get the same view-the-file affordance: the path renders
  // through the content-scanner registry (the filesystem plugin's preview
  // popover, with an inline thumbnail for images) instead of inert text. Edit
  // covers Edit/Write/StrReplace/EditNotebook, whose files exist post-run;
  // delete/move leave no resolvable path, so they keep the plain rendering.
  // Only absolute paths resolve on the devbox, so a relative one — what an
  // agent typically writes, and all a rewritten shell row has — is resolved
  // against the directory the session runs in first. `display.isPath` also
  // guards against `display.code` being the raw-JSON MCP fallback (never
  // path-shaped, so `absoluteFilePath` already refuses it, but the explicit
  // check keeps this correct if that fallback's shape ever changes).
  // `mcpPreviewSafe` keeps this affordance from silently hiding other MCP
  // arguments — see its definition above.
  const previewPath =
    (view.kind === 'read' || view.kind === 'edit') &&
    display.isPath &&
    display.code &&
    mcpPreviewSafe
      ? absoluteFilePath(display.code, workingDir)
      : null;

  // For successful edit/delete/move operations, suppress text output (e.g. "Wrote file
  // successfully.") — the status indicator already communicates success. Diffs are still shown.
  // AskUserQuestion output (answers payload or deny reason) is always suppressed; its card owns it.
  const suppressTextOutput =
    isAskUserQuestion ||
    (!isFailed && (view.kind === 'edit' || view.kind === 'delete' || view.kind === 'move'));
  // A write-equivalent shell command often carries the new bytes in the command
  // itself, so build the same diff the Write tool shows — for an append, those
  // bytes are exactly the added lines, which is what an all-added diff says.
  // (`tee` also echoes them to stdout; that copy is suppressed as edit-kind
  // output.) Only a completed call gets a diff: an interrupted or still-running
  // write has not put those bytes on disk, and the native Write row shows no
  // diff either. A command whose output isn't in its text (`date >> log`) has no
  // `content` and so no diff — the row still names the verb and the file.
  // Memoized so it keeps a stable identity across re-renders: the diff-line
  // count below derives from it and is too expensive to redo on every render
  // of the surrounding turn.
  const shellWriteDiff: ContentItem[] | null = useMemo(
    () =>
      shellFileOp &&
      shellFileOp.op !== 'read' &&
      shellFileOp.content !== undefined &&
      block.status === 'completed'
        ? [
            {
              type: 'diff',
              diff: { path: shellFileOp.path, oldText: null, newText: shellFileOp.content },
            },
          ]
        : null,
    [shellFileOp, block.status],
  );
  // An MCP write carries its bytes the same way, and unlike the native tools
  // nothing upstream turns them into a diff — the server's own result text is
  // just "wrote 12 lines". Synthesizing it here is what keeps the content
  // reachable once the row trades its argument tooltip for the path chip (see
  // `mcpWrite` above). A server that returns a real diff of its own keeps it.
  const mcpWriteDiff: ContentItem[] | null = useMemo(
    () =>
      mcpWrite && !block.content.some((c) => c.type === 'diff')
        ? [
            {
              type: 'diff',
              diff: { path: mcpWrite.path, oldText: null, newText: mcpWrite.content },
            },
          ]
        : null,
    [mcpWrite, block.content],
  );
  // Strip plain-text content items (success messages) for edit-kind tools; keep diffs.
  const visibleContent = useMemo(
    () =>
      shellWriteDiff ??
      mcpWriteDiff ??
      (suppressTextOutput ? block.content.filter((c) => c.type !== 'content') : block.content),
    [shellWriteDiff, mcpWriteDiff, suppressTextOutput, block.content],
  );
  const genericHasContent =
    visibleContent.length > 0 || (!suppressTextOutput && block.rawOutput != null);

  const statusLabel = isFailed
    ? 'failed'
    : isInProgress
      ? 'running'
      : block.status === 'cancelled'
        ? 'interrupted'
        : 'completed';
  // Background agent/task rows carry their description, summary, type, and
  // driving command/prompt on `extra` + `rawInput`. Surface that info both as a
  // hover popover on the icon and, like the other tool rows, as an expandable
  // accordion body (which additionally shows the command/prompt).
  const background = getBackgroundInfo(block, statusLabel);
  const hasBackgroundDetails = background != null;

  const outputText =
    block.content
      .filter((c) => c.type === 'content' && c.text)
      .map((c) => c.text!)
      .join('\n') || undefined;

  // A pull request a call opened or touched is the most consequential thing a
  // tool row can produce, and today it arrives as a bare URL inside the output
  // panel — behind an expander, indistinguishable from any other line. Promote
  // it onto the row itself as the PR chip, so the run's headline artifact is
  // visible (and its state readable) without expanding anything. The badge
  // comes from whichever plugin claims the URL, via the content-scanner
  // registry, so this file needs no GitHub client of its own.
  const prLinks = useMemo(() => {
    if (!outputText) return [];
    // Bound the scan and the row: a `gh pr list` prints a page of links, and
    // the row is not a list. The output panel still has all of them.
    return parseGitHubPrUrls(outputText.slice(0, PR_LINK_SCAN_LIMIT)).slice(0, MAX_PR_LINK_CHIPS);
  }, [outputText]);

  // A custom renderer owns the whole expanded body, so the row is expandable
  // as soon as there is anything to show it — args while running, output once
  // the call resolves.
  const hasContent = customRenderer
    ? input !== null || outputText != null || block.rawOutput != null
    : // The command left the header row, so the body must always be able to
      // show it — even for a read that printed nothing. Args are clipped on the
      // row, so the body owns the full text.
      genericHasContent || shellCommand != null || display.args != null;
  // Only advertise the row as expandable when opening it actually shows
  // something: a row that offers a pointer cursor and then opens onto nothing
  // reads as broken.
  const hasExpandable = hasContent || hasBackgroundDetails;

  const hasDiffs = visibleContent.some((c) => c.type === 'diff');
  // Aggregate added/removed line counts across every text diff on the row (a
  // single Edit call can carry more than one diff item) so the row itself shows
  // `+N -M` even while collapsed, without waiting for expansion. Skipped for
  // image diffs and for pathological content that `TextDiffView` won't fully
  // diff either: a count over those is meaningless, and an unbounded Myers diff
  // on the always-visible row is exactly what the guards exist to prevent.
  // `getDiffLineCounts` memoizes per diff item, so this shares its work with
  // the expanded diff view and returns null rather than a slow answer when the
  // change is too large to count. Counting `visibleContent` rather than
  // `block.content` also covers a write-equivalent shell command, whose diff is
  // synthesized.
  const diffCounts = useMemo(() => {
    if (!hasDiffs) return null;
    let added = 0;
    let removed = 0;
    for (const item of visibleContent) {
      if (item.type !== 'diff' || !item.diff || isImagePath(item.diff.path)) continue;
      if (getDiffSizeIssue(item.diff)) continue;
      const counts = getDiffLineCounts(item.diff);
      if (!counts) continue;
      added += counts.added;
      removed += counts.removed;
    }
    return { added, removed };
  }, [hasDiffs, visibleContent]);

  // Read results arrive as `cat -n`-numbered text. When the whole output
  // parses and we know the file path, render it with the same styled treatment
  // as edit diffs (path header, gutter, syntax highlighting) instead of the
  // generic Output panel. `parseReadOutput` fails closed to the generic panel
  // for anything that isn't actually `cat -n`-shaped. `display.isPath` keeps
  // this from ever feeding a non-path value (e.g. the raw-JSON MCP fallback)
  // to `ReadOutputView` as a path. Unlike `previewPath` above, this only
  // affects the expanded body (not the header row's tooltip-vs-preview
  // trade-off), so it doesn't need the `mcpPreviewSafe` gate. A shell read
  // prints the raw bytes with no gutter of its own (unless it was `cat -n`),
  // so number them here to get the same card.
  const readPath = view.kind === 'read' && display.isPath ? (display.code ?? undefined) : undefined;
  const readLines =
    readPath && !isFailed && outputText
      ? shellFileOp?.op === 'read'
        ? // `cat -n` prints its own gutter; anything else needs one. Either way
          // the shell's output is uncapped, so the card's ceiling applies.
          capReadLines(
            shellFileOp.numbered
              ? parseReadOutput(outputText)
              : numberLines(outputText, shellFileOp.startLine),
          )
        : parseReadOutput(outputText)
      : null;

  const row = (
    <>
      <div
        onClick={hasExpandable ? () => setExpanded((v) => !v) : undefined}
        className={cn(
          // Align the label and the monospaced command on a shared text
          // baseline: the sans label and the mono command have different
          // font metrics, so centering their (identical) line boxes leaves
          // the glyphs visibly offset. Non-text children (status dot, icon,
          // spinner, chips) opt back into centering via `self-center`.
          'group/tool relative -ml-[21px] flex items-baseline gap-2 rounded-lg px-2.5 py-0.5 text-xs text-muted-foreground',
          hasExpandable && 'cursor-pointer transition-colors hover:bg-muted/[0.06]',
        )}
      >
        <div
          className={cn(
            'h-1.5 w-1.5 shrink-0 self-center rounded-full',
            isFailed ? 'bg-status-error/70' : isInProgress ? 'bg-status-info/70' : 'bg-border/60',
          )}
        />
        {background ? (
          <HoverCard openDelay={150} closeDelay={100}>
            <HoverCardTrigger asChild>
              {/* Opening the popover shouldn't also toggle the row's expansion. */}
              <span
                role="button"
                tabIndex={0}
                aria-label="Background task details"
                className="shrink-0 cursor-help self-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Icon className={cn('h-3.5 w-3.5', isFailed && 'text-status-error')} />
              </span>
            </HoverCardTrigger>
            <HoverCardContent
              align="start"
              className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
              aria-label="Background task details"
            >
              <BackgroundTaskPopover info={background} icon={Icon} />
            </HoverCardContent>
          </HoverCard>
        ) : (
          <Icon
            className={cn('h-3.5 w-3.5 shrink-0 self-center', isFailed && 'text-status-error')}
          />
        )}
        {/* Our own words are short by construction and always fit; an agent's
            title is a whole sentence, so that row lets it clip rather than
            pushing the chips beside it off the end. */}
        <span
          className={cn(titleLabel ? 'min-w-0 shrink truncate' : 'shrink-0')}
          title={titleLabel ? view.title : undefined}
          data-testid={titleLabel ? 'tool-title-label' : undefined}
        >
          {prefix}
        </span>
        {shellSummary ? (
          // The summary replaces the command entirely: the subject and its
          // qualifiers are what the row is about, and the command itself lives
          // on the `shell` badge's hover panel and in the expanded body.
          <ShellHeadlineView step={shellSummary.headline} repo={repo} />
        ) : (
          code &&
          (previewPath ? (
            <PreviewPathChip
              path={previewPath}
              label={codeLabel}
              dir={codeDir}
              className="min-w-0 shrink truncate font-mono"
            />
          ) : mcpName ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <code
                  dir={codeDir}
                  className={cn(
                    'min-w-0 shrink truncate font-mono cursor-default',
                    codeTextClass,
                    codeChipClass,
                  )}
                >
                  {codeNode}
                </code>
              </TooltipTrigger>
              {/* Unlike the generic command tooltip below, this always renders
                  (it isn't gated on `fullCode`'s length threshold): an MCP
                  call's arguments are otherwise invisible even when short, and
                  the raw `mcp__server__tool` name the friendly prefix replaces
                  is otherwise not shown anywhere. */}
              <TooltipContent className="max-w-xl min-w-[22rem] overflow-hidden p-0">
                <div className="flex items-center gap-1.5 border-b border-border/40 bg-muted/40 py-1 pl-2.5 pr-1">
                  <Wrench className="h-3 w-3 shrink-0 text-muted-foreground/70" />
                  <span className="flex-1 truncate text-3xs font-medium uppercase tracking-wide text-muted-foreground/70">
                    MCP Tool
                  </span>
                  <span onClick={(e) => e.stopPropagation()}>
                    <CopyIconButton
                      text={mcpArgsPretty ?? ''}
                      testId="mcp-args-copy"
                      ariaLabel="Copy MCP tool arguments"
                      className="h-6 w-6 text-muted-foreground hover:bg-muted hover:text-foreground"
                    />
                  </span>
                </div>
                <div
                  className="truncate border-b border-border/30 px-3 py-1.5 font-mono text-3xs text-muted-foreground/60"
                  title={rawToolName}
                >
                  {rawToolName}
                </div>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all px-3 py-2.5 font-mono text-xs leading-relaxed text-foreground/85">
                  <InlineHighlightedCode code={mcpArgsPretty ?? ''} lang="json" />
                </pre>
              </TooltipContent>
            </Tooltip>
          ) : fullCode ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <code
                  dir={codeDir}
                  className={cn(
                    'min-w-0 shrink truncate font-mono cursor-default',
                    codeTextClass,
                    codeChipClass,
                  )}
                >
                  {codeNode}
                </code>
              </TooltipTrigger>
              {/* Render the tooltip as a compact command card: a header strip
                  (terminal glyph + label + copy action) over a code surface —
                  the same panel language the expanded tool bodies use. `p-0`
                  strips the popover's default padding so the header can span
                  edge to edge; `overflow-hidden` keeps it inside the radius. */}
              <TooltipContent className="max-w-xl min-w-[22rem] overflow-hidden p-0">
                <ShellCommandCard
                  command={fullCode}
                  interpreter={display.interpreter}
                  lang={display.lang}
                />
              </TooltipContent>
            </Tooltip>
          ) : (
            <code
              dir={codeDir}
              className={cn('min-w-0 shrink truncate font-mono', codeTextClass, codeChipClass)}
            >
              {codeNode}
            </code>
          ))
        )}
        {rowArgs && (
          // What the skill was pointed at belongs next to its name: the row is
          // otherwise identical for every call of the same skill. `flex-1`
          // (basis 0) makes the args take only the space left over, so they
          // clip alone — a shrink factor would instead spread the overflow and
          // clip the skill's name too, on a row wide enough to show it whole.
          // The expanded body always has the full text.
          <span
            data-testid="tool-args"
            className="min-w-0 flex-1 truncate font-mono text-muted-foreground/50"
          >
            {rowArgs}
          </span>
        )}
        {commandTimeout && <CommandTimeoutBadge timeout={commandTimeout} />}
        {diffCounts && <DiffLineCountBadge counts={diffCounts} />}
        {shellSummary && shellSummary.steps.length > 1 && (
          // The headline speaks for the first command only; this says how many
          // followed, so a chain never looks like a single call. It is text at
          // the row's own size, so it keeps the row's baseline — centering it
          // like the badges beside it would leave the digits sitting low.
          <span
            data-testid="shell-summary-more"
            className="shrink-0 tabular-nums text-muted-foreground/45"
            title={`${shellSummary.steps.length} commands`}
          >
            +{shellSummary.steps.length - 1}
          </span>
        )}
        {shellCommand && (
          // Be honest about provenance: this row is our words, not a tool's —
          // the agent got there with a shell command. Hovering the badge shows
          // exactly which one.
          <ShellOriginBadge
            command={unwrapShellCommand(shellCommand)}
            interpreter={shellInterpreter}
            testId={shellFileOp ? 'shell-file-op-badge' : 'shell-summary-badge'}
          />
        )}
        {prLinks.map((link) => (
          // The chip is its own link and hover target, so a click on it opens
          // the PR rather than toggling the row it happens to sit on.
          <span
            key={link.externalId}
            className="shrink-0"
            data-testid="tool-call-pr-link"
            onClick={(e) => e.stopPropagation()}
          >
            <ScannedUrl url={link.url}>#{link.number}</ScannedUrl>
          </span>
        ))}
        {readFirstRetry && <ReadFirstRetryBadge retry={readFirstRetry} />}
        {!!block.extra?.backgroundTaskId && (
          <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-3xs text-muted-foreground">
            background
          </span>
        )}
        {/* The chip marks a failure the row's words don't already carry; when
            the prefix reads "Couldn't push", repeating "failed" is noise. */}
        {isFailed && !prefixSaysFailed && (
          <span className="shrink-0 text-status-error">failed</span>
        )}
        {block.status === 'cancelled' && (
          <span className="shrink-0 text-muted-foreground/60">interrupted</span>
        )}
        {isInProgress && (
          <Spinner size="xs" className="ml-auto self-center text-muted-foreground/40" />
        )}
        {block.sourceEvent && (
          <span className="ml-auto self-center opacity-0 transition-opacity group-hover/tool:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>
      <AnimatedCollapse open={expanded && (hasContent || hasBackgroundDetails)}>
        <div className={cn('ml-5 mt-0.5 mb-1 space-y-1', hasDiffs && 'space-y-2')}>
          {customRenderer ? (
            <Suspense
              fallback={
                <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground/60">
                  <Spinner size="xs" /> Loading…
                </div>
              }
            >
              <customRenderer.component
                toolName={toolName}
                status={block.status}
                input={input}
                outputText={outputText ?? null}
                agentId={agentId}
              />
            </Suspense>
          ) : (
            <>
              {/* The headline can only speak for the first command, so a chain
                  accounts for the rest here before the raw text. */}
              {shellSummary && shellSummary.steps.length > 1 && (
                <ShellStepsPanel steps={shellSummary.steps} repo={repo} />
              )}
              {/* The row clips the args to one line; here they read in full,
                  exactly as written, and can be copied. */}
              {display.args && (
                <DetailPanel label="Arguments" text={display.args} testId="tool-args-panel" />
              )}
              {shellCommand && (
                <DetailPanel
                  label="Command"
                  icon={SquareTerminal}
                  text={shellCommand}
                  testId="shell-file-op-command"
                />
              )}
              {hasBackgroundDetails && background && <BackgroundTaskDetail info={background} />}
              {isFailed && outputText && <ToolCallErrorView text={outputText} />}
              {isFailed && !outputText && <ToolCallErrorView rawOutput={block.rawOutput} />}
              {!isFailed && hasDiffs && (
                <ToolCallContentView content={visibleContent} rawOutput={undefined} />
              )}
              {!isFailed &&
                !hasDiffs &&
                outputText &&
                !suppressTextOutput &&
                (readPath && readLines ? (
                  <ReadOutputView path={readPath} lines={readLines} />
                ) : (
                  <div className="rounded-lg border border-border/20 bg-muted/10 overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-border/20 bg-muted/15 px-3 py-1.5">
                      <span className="flex-1 truncate text-xs text-muted-foreground/60">
                        Output
                      </span>
                      <CopyButton text={outputText} />
                    </div>
                    <pre className="max-h-64 overflow-auto p-3 font-mono text-xs whitespace-pre-wrap leading-relaxed text-foreground/60">
                      <AnsiText text={outputText} />
                    </pre>
                  </div>
                ))}
              {!isFailed &&
                !hasDiffs &&
                !outputText &&
                !suppressTextOutput &&
                block.rawOutput != null && (
                  <ToolCallContentView content={visibleContent} rawOutput={block.rawOutput} />
                )}
            </>
          )}
        </div>
      </AnimatedCollapse>
    </>
  );

  // A call that named its own directory republishes it, so everything under the
  // row — a summarized subject, a diff or read card — resolves against the same
  // place the header path did. A row without one adds nothing to the tree.
  return rowWorkingDir ? (
    <WorkingDirProvider workingDir={rowWorkingDir}>{row}</WorkingDirProvider>
  ) : (
    row
  );
}
